import { useNavigate } from "react-router-dom";
import { Masthead } from "@/components/Masthead";
import { Footer } from "@/components/Footer";
import { ShieldCheck, ListChecks, Eye, Lock, FileText, RefreshCw, Scale } from "lucide-react";

const SectionHeading = ({ icon: Icon, title, subtitle }) => (
  <div className="flex items-start gap-4 mb-4">
    <div className="shrink-0 mt-1 p-2.5 rounded-lg bg-scoop-blue/10 text-scoop-blue">
      <Icon className="w-5 h-5" />
    </div>
    <div>
      <h2 className="font-display text-xl font-bold text-scoop-ink leading-tight">{title}</h2>
      {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
    </div>
  </div>
);

const Pill = ({ children }) => (
  <span className="inline-block text-[0.65rem] font-bold tracking-widest uppercase px-2 py-0.5 rounded bg-scoop-blue/10 text-scoop-blue">
    {children}
  </span>
);

export const GuidelinesPage = () => {
  const navigate = useNavigate();

  return (
    <div className="scoop-page min-h-screen bg-scoop-cream" data-testid="guidelines-page">
      <Masthead
        activeCategory=""
        onCategoryChange={(cat) => navigate(cat && cat !== "today" ? `/?category=${cat}` : "/")}
      />

      <div className="max-w-[760px] mx-auto px-5 md:px-8 py-10 md:py-14">
        {/* Hero */}
        <div className="mb-10 pb-8 border-b border-scoop-border">
          <Pill>Editorial &amp; Safety Guidelines</Pill>
          <h1 className="font-display text-3xl md:text-4xl font-black text-scoop-ink mt-3 leading-tight">
            How we keep <em className="italic text-scoop-blue">The Scoop</em> safe for kids
          </h1>
          <p className="text-gray-600 mt-3 leading-relaxed">
            Every story you read on The Scoop passes through several checks before it
            reaches a kid&rsquo;s screen. Here&rsquo;s exactly what those checks do, what
            they block, and how we keep a record of every decision.
          </p>
          <p className="text-xs text-scoop-muted mt-3">Last updated: July 2026</p>
        </div>

        {/* 1. What we publish */}
        <section className="mb-10">
          <SectionHeading icon={ShieldCheck} title="What we publish" subtitle="The shape of a Scoop story" />
          <div className="space-y-3 text-[15px] text-gray-700 leading-relaxed pl-1">
            <p>
              We aim for stories that a curious 6&ndash;9 year old can enjoy on their own,
              and that a parent would feel good about over the dinner table: real
              discoveries, helpers and heroes, sports victories, animals, space, climate
              progress, and community wins.
            </p>
            <p>
              Our standard is <strong>affirmative</strong>: a story isn&rsquo;t published because
              nothing bad was found in it &mdash; it&rsquo;s published because its subject
              belongs to the kid world in the first place. When we&rsquo;re not sure, we
              don&rsquo;t run it. There&rsquo;s always more news; a parent&rsquo;s trust
              doesn&rsquo;t come back as easily.
            </p>
            <p>
              We write at roughly a 2nd&ndash;4th grade reading level, in short sentences,
              with natural language. No clickbait, no scare tactics, no marketing.
              That span is deliberately wide, because a 6-year-old is still decoding
              words while a 9-year-old reads fluently &mdash; so every story can also be
              read aloud, with each word highlighted as it&rsquo;s spoken.
            </p>
            <p>
              We&rsquo;re honest that one reading level cannot serve that whole range
              well. Offering a simpler and a fuller version of each story is the most
              useful thing on our roadmap.
            </p>
          </div>
        </section>

        {/* 2. What we don't publish */}
        <section className="mb-10">
          <SectionHeading icon={ListChecks} title="What we don't publish" subtitle="Hard limits — no exceptions for framing" />
          <ul className="space-y-3 text-[15px] text-gray-700 leading-relaxed pl-1">
            <li><strong>Graphic violence, war casualties, weapons, or detailed death.</strong></li>
            <li><strong>Anything that could cause fear, anxiety, or nightmares</strong> &mdash; disasters, missing children, trauma framed scarily.</li>
            <li><strong>Sexual content, profanity, or slurs of any kind.</strong></li>
            <li><strong>Substances</strong> &mdash; drugs, alcohol, vaping, overdoses.</li>
            <li>
              <strong>Real-world elections and partisan politics</strong> &mdash; including local elections, voting,
              ballots, polling stations, campaigns, candidates, and party politics.
              We don&rsquo;t soften this with civic-education framing &mdash; if the story is
              about a real election, we don&rsquo;t run it.
            </li>
            <li>
              <strong>The White House, Capitol, Congress or Parliament as political venues.</strong>
              Pure history or architecture (e.g. how a building was built) is fine.
            </li>
            <li>
              <strong>Sitting or recent politicians in their political role.</strong> Apolitical
              ceremonies and historical biography are okay.
            </li>
            <li>
              <strong>Gambling, betting, prediction markets, or financial speculation</strong> framed as entertainment.
            </li>
            <li>
              <strong>Sports-world politics</strong> &mdash; referee disputes, federation protests,
              doping scandals, contract drama. The <em>play</em> side of sports is what we
              cover: matches, athletes, achievements, comebacks.
            </li>
            <li>
              <strong>Corporate and business news</strong> &mdash; layoffs, earnings, stock prices,
              mergers, lawsuits, fines, executive changes. Adult-world machinery isn&rsquo;t
              children&rsquo;s news, even told gently. (A fun new gadget or invention kids would
              actually use is fine &mdash; the corporate politics around the company is not.)
            </li>
            <li>
              <strong>Concepts a typical 10&ndash;year&ndash;old can&rsquo;t meaningfully grasp</strong> when they&rsquo;re the central subject
              (e.g. pardons, impeachment, monetary policy).
            </li>
            <li>
              <strong>Framings that model behaviour we wouldn&rsquo;t want kids to copy</strong> &mdash; e.g. celebrating a smart bet.
            </li>
          </ul>
        </section>

        {/* 3. How we check */}
        <section className="mb-10">
          <SectionHeading icon={Eye} title="How every story is checked" subtitle="Defence in depth — three independent layers" />
          <div className="space-y-5 text-[15px] text-gray-700 leading-relaxed">
            <div className="bg-white border border-scoop-border rounded-lg p-5">
              <p className="font-bold text-scoop-ink mb-1">1. The rewrite stage</p>
              <p>
                Before a story is written for kids, our AI is given the full safety
                rulebook in its instructions. If the source article is centred on
                something we don&rsquo;t cover (an election, a war casualty, a
                gambling story), the rewrite is refused and the story never enters
                the pipeline.
              </p>
            </div>
            <div className="bg-white border border-scoop-border rounded-lg p-5">
              <p className="font-bold text-scoop-ink mb-1">2. A keyword backstop</p>
              <p>
                The rewritten story is then scanned for unambiguous unsafe phrases
                &mdash; things like &ldquo;head to the polls&rdquo;, &ldquo;polling station&rdquo;,
                &ldquo;the White House&rdquo;, &ldquo;casualty count&rdquo;, &ldquo;ballot box&rdquo;.
                If any are present, the story is rejected even if our judgment AI
                missed it.
              </p>
            </div>
            <div className="bg-white border border-scoop-border rounded-lg p-5">
              <p className="font-bold text-scoop-ink mb-1">3. A separate kid-safety judge</p>
              <p>
                A second AI then asks two questions in order. First, the
                <strong> affirmative gate</strong>: does this story&rsquo;s subject belong to the
                kid world at all &mdash; nature, space, discovery, sports, arts, helpers,
                young achievers? If not, it fails, no matter how gently it&rsquo;s written.
                Second, the <strong>harm screen</strong>: emotional safety, violence, mature
                content, gambling, politics, religious disputes, age-appropriateness, and
                the values the story would model for a child. A story is published{" "}
                <strong>only</strong> when both gates explicitly pass. If the judge can&rsquo;t
                reach a confident decision, the story is <strong>held back</strong> &mdash;
                we never default to publishing.
              </p>
            </div>
          </div>
        </section>

        {/* 4. Auditable */}
        <section className="mb-10">
          <SectionHeading icon={FileText} title="Auditable by design" subtitle="Every decision is logged" />
          <div className="space-y-3 text-[15px] text-gray-700 leading-relaxed pl-1">
            <p>
              Every safety decision &mdash; pass, block on content, or hold due to an
              infrastructure issue &mdash; is written to a moderation log with the
              headline, the dimension(s) that triggered the block, and the
              classifier&rsquo;s reason in plain English. This lets us answer the
              question &ldquo;why did this story run or not run?&rdquo; for any article we&rsquo;ve ever shown.
            </p>
            <p>
              Regulators (notably UK Ofcom under the Online Safety Act) increasingly
              expect services used by children to <em>demonstrate</em> their moderation
              system is effective, not just that bad content gets removed later. The
              moderation log is how we do that.
            </p>
          </div>
        </section>

        {/* 5. Regression-tested */}
        <section className="mb-10">
          <SectionHeading icon={RefreshCw} title="Tested against itself" subtitle="So our rules don't quietly drift" />
          <div className="space-y-3 text-[15px] text-gray-700 leading-relaxed pl-1">
            <p>
              We keep a fixed corpus of example stories that <em>must</em> pass
              (a library cat, a sea-turtle rescue, a young athlete&rsquo;s comeback) and
              stories that <em>must</em> be blocked (election coverage, gambling
              framings, war casualty counts, civic-education framings of partisan
              politics).
            </p>
            <p>
              Every time we change a rule &mdash; tighten one, loosen one, add a new
              category &mdash; we run the whole corpus through the safety system and
              confirm nothing slipped. If a story that should pass starts being
              blocked, or vice versa, we fix the rule before it ships.
            </p>
          </div>
        </section>

        {/* 6. Privacy */}
        <section className="mb-10">
          <SectionHeading icon={Lock} title="Privacy" subtitle="What we collect, what we don't" />
          <div className="space-y-3 text-[15px] text-gray-700 leading-relaxed pl-1">
            <p>
              The Scoop is designed for under-13 readers, so the site itself is built
              to know as little as possible:
            </p>
            <ul className="space-y-2 list-disc pl-5">
              <li>
                <strong>No analytics, no session recording, no tracking pixels.</strong>{" "}
                We removed third-party analytics entirely &mdash; nothing watches how a
                child uses this site.
              </li>
              <li>
                <strong>No location prompts.</strong> The weather bar shows a general
                city by default. If you tap &ldquo;My area,&rdquo; your browser asks
                permission, we use the coordinates once to fetch the local forecast,
                and we never store them. No outside geocoding service is contacted
                from your browser.
              </li>
              <li>
                <strong>Accounts are for parents.</strong> Kids can read everything
                without signing in. Email digest subscribers can unsubscribe with a
                single click, and we never share email addresses with anyone.
              </li>
              <li>
                <strong>Our safety log stores story decisions, not reader data.</strong>{" "}
                The moderation record described above is about articles &mdash; it
                contains nothing about who read them.
              </li>
            </ul>
            <p>
              The amended COPPA Rule took effect in June 2025, with full compliance
              required from April 2026. Our{" "}
              <a href="/privacy" className="text-scoop-blue underline underline-offset-2 font-semibold">
                privacy notice
              </a>{" "}
              sets out what is stored, where, and for how long.
            </p>
          </div>
        </section>

        {/* 7. Standards we build to */}
        <section className="mb-10">
          <SectionHeading
            icon={Scale}
            title="The standards we build to"
            subtitle="What the rules ask for, and the choice we made"
          />
          <div className="space-y-5 text-[15px] text-gray-700 leading-relaxed pl-1">
            <p>
              Children&rsquo;s products are governed by a growing set of rules about
              privacy, safety and design. Most of them are about design choices rather
              than paperwork, so rather than list statutes, here is what each one asks
              for and what we actually did about it.
            </p>

            <div className="bg-white border border-scoop-border rounded-lg p-5">
              <p className="font-bold text-scoop-ink mb-1">
                Children&rsquo;s privacy &mdash; COPPA (US)
              </p>
              <p>
                The rule governs <em>collecting</em> personal information from children
                under 13, so our answer is to not collect any. Kids never sign in. In
                The Scoop app, a child&rsquo;s first name, avatar, reading progress,
                saved stories, poll answers and badges are stored on their own device
                and are never uploaded &mdash; a parent can see the full list and erase
                it from the Parent Zone. Accounts, email and sharing are parent-only.
                Our servers necessarily see an IP address when a page is requested; we
                use that to serve the page and nothing else &mdash; no profiles, no
                behavioural targeting, no sale or sharing of data.
              </p>
            </div>

            <div className="bg-white border border-scoop-border rounded-lg p-5">
              <p className="font-bold text-scoop-ink mb-1">
                Design that doesn&rsquo;t pressure children &mdash; EU Digital Services
                Act Article 28, the UK ICO Children&rsquo;s Code, and US state
                age-appropriate design codes
              </p>
              <p>
                The European Commission&rsquo;s July 2025 guidelines recommend that
                engagement mechanics such as streaks be turned <em>off</em> by default
                for minors, and several US states now prohibit &ldquo;dark
                patterns&rdquo; aimed at children. This one changed our product. We
                removed the daily reading streak: a streak&rsquo;s pull comes from the
                fear of breaking it, and a seven-year-old doesn&rsquo;t control their
                own schedule. What we show instead is the total number of stories
                finished &mdash; a number that only ever goes up, so there is nothing to
                lose and nothing to defend. A weekly view of days read is available, but
                a parent has to switch it on. There are no leaderboards, no comparison
                with other children, no autoplay, and no notifications that imply a
                child has fallen behind.
              </p>
            </div>

            <div className="bg-white border border-scoop-border rounded-lg p-5">
              <p className="font-bold text-scoop-ink mb-1">
                Apple&rsquo;s Kids Category requirements
              </p>
              <p>
                Anything meant for a grown-up sits behind a parental gate: a maths
                challenge that changes every time, then a four-digit code, and the zone
                re-locks the moment the app goes to the background. Every outbound link,
                the sign-in flow and the sharing tools live behind it. There is no
                advertising, nothing to buy, and no third-party analytics or advertising
                code anywhere in the app &mdash; including on the parent side, where the
                rules still apply.
              </p>
            </div>

            <div className="bg-white border border-scoop-border rounded-lg p-5">
              <p className="font-bold text-scoop-ink mb-1">
                Honest claims about AI &mdash; US FTC
              </p>
              <p>
                The FTC has been active against companies making AI claims they
                can&rsquo;t back up. Our stories carry the label{" "}
                <strong>&ldquo;Checked for ages 6&ndash;9&rdquo;</strong> rather than
                &ldquo;AI checked &amp; safe.&rdquo; The checks described above are real
                and we stand behind them, but they are automated, and an unqualified
                promise of safety is not something any automated system can honestly
                make.
              </p>
            </div>

            <div className="bg-white border border-scoop-border rounded-lg p-5">
              <p className="font-bold text-scoop-ink mb-1">
                Usability research for children &mdash; Nielsen Norman Group
              </p>
              <p>
                Not a regulation, but it settles arguments. Children react badly to
                content pitched even one grade off, so the app targets a narrow band and
                says so. Beginning readers can&rsquo;t interpret elapsed time, so
                stories say &ldquo;New today&rdquo; rather than &ldquo;18h ago.&rdquo;
                They can&rsquo;t spell reliably, so search forgives typos and browsing by
                picture comes first. They read icons literally, so buttons carry words.
                And because many of our readers are still learning to decode text, every
                story can be read aloud with the words highlighted as they are spoken.
              </p>
            </div>

            <div className="bg-[#FFF7E8] border border-[#F5D9A8] rounded-lg p-5">
              <p className="font-bold text-[#92600A] mb-1">What we don&rsquo;t claim</p>
              <p className="text-[#7A5209]">
                This page describes our own practice. It isn&rsquo;t a certification, no
                regulator has audited us, and none of it is legal advice. We&rsquo;re a
                small team and the rules are moving quickly &mdash; if you think
                we&rsquo;ve got something wrong, or that a claim here doesn&rsquo;t match
                what the product actually does, please tell us and we&rsquo;ll correct
                it. We update this page when our practice changes, not just when the law
                does.
              </p>
            </div>
          </div>
        </section>

        {/* Closing */}
        <div className="bg-[#F0FBF5] border-l-[3px] border-scoop-sage rounded-r p-5 mt-10">
          <p className="text-sm text-[#1A5C3A] leading-relaxed">
            Found a story that shouldn&rsquo;t have made it through? Every story has a{" "}
            <strong>&ldquo;Something not right? Tell us&rdquo;</strong> link at the bottom &mdash;
            reports are anonymous, they go straight to our team, and we fix the rule,
            not just remove the story.
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default GuidelinesPage;
